import React, { useEffect, useState } from "react";

import { gql, useMutation, useQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";

import { NotificationType } from "../../../models/notifications";
import { GET_NOTIFICATIONS, NOTIFICATION_READ } from "./queries";
import { IconJumbotron } from "../../common/icons";
import { timeAgo } from "../../../middleware/dates";
import { LoaderDots } from "../../../middleware/loaders";
import { getAuthCookie } from "../../../middleware/cookie-manager";

type NotificationQueryType = {
  notifications: NotificationType[];
  refetch: any;
};

const Notifications: React.FC = () => {
  const history = useHistory();
  const user = getAuthCookie();
  const [unread_num, setUnreadNum] = useState(0);
  const [notifications, setNotifications] = useState<NotificationType[] | null>(
    null,
  );

  const {
    data: notifications_data,
    refetch,
    loading,
  } = useQuery<NotificationQueryType>(GET_NOTIFICATIONS, {
    variables: { limit: 100 },
    fetchPolicy: "no-cache",
  });
  const [readNotification] = useMutation(NOTIFICATION_READ);

  useEffect(() => {
    updateBadge();
    if (notifications_data && notifications_data.notifications) {
      setNotifications(notifications_data.notifications);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications_data]);

  const updateBadge = (num: number = 0) => {
    if (!notifications_data) return false;
    let notifications_num = notifications_data.notifications.filter(
      (n) => n.readed === false,
    ).length;
    const notifications_badge = document.getElementById(
      "notifications_badge",
    ) as HTMLElement;

    notifications_num = notifications_num - num;
    setUnreadNum(notifications_num);
    if (notifications_num > 0) {
      notifications_badge.innerHTML = notifications_num.toLocaleString();
    } else {
      notifications_badge.innerHTML = "";
    }
  };

  const redirect = (action: string, time: number = 500) => {
    updateBadge(1);
    setTimeout(() => {
      history.push(action);
    }, time);
  };

  const markAsRead = (ev: {
    currentTarget: HTMLDivElement | HTMLButtonElement;
    target: HTMLDivElement | HTMLButtonElement;
  }) => {
    const target_el = ev.target;
    ///avoid running on click on the button cta
    if (target_el.tagName === "BUTTON") return;

    const type = ev.currentTarget.dataset.type;
    const action = ev.currentTarget.dataset.action;
    const notification_id = ev.currentTarget.dataset.notification_id;
    readNotification({ variables: { id: notification_id } });
    if (!type || !action) {
      return;
    }

    switch (type) {
      case "link":
        redirect(action);
        break;
      case "read_only":
      default:
        refetch();
        break;
    }
  };

  const removeOne = async (notification_id: string) => {
    readNotification({
      variables: { id: notification_id, delete: true },
    });
    setNotifications(
      notifications!.filter((n) => n.id !== notification_id) || null,
    );
  };
  const markAll = async () => {
    await readNotification();
    refetch();
  };

  const removeAll = async () => {
    readNotification({
      variables: { delete: true },
    });
    setNotifications([]);
  };

  return (
    <main>
      <div className="container first-container last-container justify-content-center d-flex">
        <div className="notifications-container list-group">
          <h2 className="d-flex justify-content-between">
            Notifications{" "}
            <button
              className="btn btn-link "
              onClick={markAll}
              disabled={unread_num === 0}
            >
              Mark all as read
            </button>
          </h2>

          {notifications && notifications.length > 0 ? (
            notifications.map((notification: NotificationType) => {
              if (!notification.cta || !notification.cta.mutation) {
                return (
                  <NotificationButton
                    notification={notification}
                    markAsRead={markAsRead}
                    isTeacher={user?.is_teacher}
                    deleteFunction={removeOne}
                  />
                );
              }
              return (
                <NotificationItem
                  key={`notification.id_${notification.id}`}
                  deleteFunction={removeOne}
                  notification={notification}
                  markAsRead={markAsRead}
                  refetch={refetch}
                  isTeacher={user?.is_teacher}
                />
              );
            })
          ) : loading ? (
            <LoaderDots text="Loading notifications" />
          ) : (
            <IconJumbotron
              txt="No notifications"
              icon="far fa-bell-slash"
              classes="my-4"
            />
          )}
          {user && user.is_teacher && notifications && notifications.length ? (
            <button
              className="btn btn-link text-danger mt-3"
              onClick={removeAll}
              disabled={
                !notifications || (notifications && notifications.length === 0)
              }
            >
              Remove all notifications
            </button>
          ) : null}
        </div>
      </div>
    </main>
  );
};

const NotificationItem: React.FC<{
  notification: NotificationType;
  markAsRead: any;
  refetch: any;
  deleteFunction: any;
  isTeacher?: boolean;
}> = ({ notification, markAsRead, refetch, deleteFunction, isTeacher }) => {
  const CTAMUTATION = gql`
    ${notification.cta!.mutation}
  `;
  const [readNotification] = useMutation(NOTIFICATION_READ);
  const [runCta, { loading }] = useMutation(CTAMUTATION);

  const runMutation = async () => {
    const variables = JSON.parse(notification.cta!.variables);
    runCta({ variables });
    await readNotification({
      variables: { id: notification.id, called: true },
    });
    refetch();
  };

  return (
    <NotificationButton
      notification={notification}
      markAsRead={markAsRead}
      mutation={runMutation}
      loading={loading}
      isTeacher={isTeacher}
      deleteFunction={deleteFunction}
    />
  );
};

const NotificationButton: React.FC<{
  notification: NotificationType;
  markAsRead: any;
  mutation?: any;
  loading?: boolean;
  deleteFunction?: any;
  isTeacher?: boolean;
}> = ({
  notification,
  markAsRead,
  mutation,
  loading,
  deleteFunction,
  isTeacher,
}) => {
  return (
    <div
      className={`list-group-item hovered list-group-item-light  ${
        notification.readed ? "" : "active"
      }  notification_item ${notification.cta && mutation ? "has-cta" : ""}`}
    >
      <button
        className={`btn btn_close ${isTeacher ? "" : "d-none"}`}
        onClick={() => deleteFunction(notification.id)}
      >
        <i className="fas fa-times"></i>
      </button>
      <button
        className="btn btn-backdrop"
        onClick={markAsRead}
        data-action={notification.action}
        role="link"
        data-notification_id={notification.id}
        data-type={notification.type}
      ></button>
      <div className="d-flex w-100 justify-content-between align-items-center">
        <i
          className={`far mr-3 fa-circle ${
            notification.readed ? "opacity_0" : "text-white"
          }`}
        ></i>
        <p
          className={`text-left w-100 m-0 ${
            notification.readed ? "text-black" : "text-white"
          }`}
        >
          {notification.message}
        </p>
        <div className="ctas_time">
          <small className="text-nowrap">
            | {timeAgo(notification.created_at)}
          </small>
        </div>
        {notification.cta && mutation ? (
          <button
            className={`btn btn-cta ${
              notification.cta.called
                ? "disabled btn-outline-danger"
                : "btn-danger"
            } btn_cta btn-sm`}
            disabled={notification.cta.called || loading}
            onClick={mutation}
          >
            {loading
              ? "Sending..."
              : notification.cta.called
                ? notification.cta.text_done
                : notification.cta.text}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default Notifications;
